/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { EventDispatcher } from '../events';
import { Logger } from '../logger';
import { xorDecode } from '../util';

const logger = Logger.getInstance();
const KEY = 'NDLS';

interface BrandSafetyEvents {
  BrandSafetyComplianceViolation: {
    detectedWords: string[];
  };
  BrandSafetyComplianceRestored: {};
}

/**
 * A banned-word occurrence located in the DOM, with its position for viewport tests.
 * `clippers` are the ancestor elements that clip their overflow (scroll/hidden/auto/clip),
 * cached once at scan time so the visibility check can tell whether the word is hidden
 * inside a scrolled-away region of a carousel/scroller rather than genuinely on screen.
 */
type Occurrence = { word: string; range: Range; element: Element | null; clippers: Element[] };

/** Predicate supplied by the caller (placement) for the outermost visibility gate. */
type VisibilityPredicate = () => boolean;

const VIEWPORT_BUFFER_PX = 50;
const DEBOUNCE_MS = 200;

/**
 * Brand safety monitor for a single campaign's blocked keywords.
 *
 * One instance is shared across every placement of the campaign (occurrences are
 * page-global, so the expensive DOM scan is shared). Visibility, however, is
 * per-placement, so the cheap viewport check and the placement-visible gate take a
 * predicate at call time rather than caching a single shared boolean.
 *
 * Cost model:
 *  - The expensive scan (`findOccurrences`) runs lazily: only when the DOM is marked
 *    dirty (initial activation, or a content mutation), and only while a placement is
 *    visible. Mutations are debounced; they never trigger a synchronous rescan.
 *  - The cheap viewport check (`checkVisibility`) runs on debounced scroll/resize and
 *    after each rescan. It re-tests the already-found occurrences against the viewport
 *    and never re-scans the DOM.
 *  - A banned word only matters when visible: both key-value suppression and post-render
 *    blur derive from "is a banned word currently in the viewport?".
 */
class BrandSafety extends EventDispatcher<BrandSafetyEvents> {
  private blockedKeywords: string[] = [];
  private blockedKeywordData: string;
  private contentSelectors: string;
  private debounceTimeout: number | null = null;
  private scrollHandler: (() => void) | null = null;
  private resizeHandler: (() => void) | null = null;
  private currentOccurrences: Occurrence[] = [];
  private hasVisibleBlockedKeywords: boolean = false;
  private contentDirty: boolean = true;
  private mutationObserver: MutationObserver | null = null;
  // One IntersectionObserver per scrolling container that holds an occurrence, so we
  // re-check visibility when a word is scrolled into/out of a carousel's clipped band
  // (window scroll alone does not fire for inner scrolling).
  private scrollObservers: IntersectionObserver[] = [];
  private isInitialised: boolean = false;
  // Visibility predicate from the most recent caller; used by debounced scroll/resize/
  // mutation handlers, which have no caller context of their own.
  private isPlacementVisible: VisibilityPredicate = () => true;

  constructor(blockedKeywordData: string, contentSelectors: string = '') {
    super();
    this.blockedKeywordData = blockedKeywordData;
    this.contentSelectors = contentSelectors;
  }

  public activate(): void {
    if (this.isInitialised) {
      return;
    }

    if (!this.blockedKeywordData.trim()) {
      return;
    }

    const decodedData = xorDecode(this.blockedKeywordData, KEY);
    if (!decodedData) {
      logger.warn('Failed to decode blocked keywords data');
      return;
    }

    logger.debug('Brand Safety monitoring activated');
    this.blockedKeywords = decodedData
      .split(',')
      .map((keyword) => keyword.trim())
      .filter((keyword) => keyword.length > 0);
    logger.debug(`Brand Safety blocked keywords: ${this.blockedKeywords.join(', ')}`);
    if (this.blockedKeywords.length === 0) {
      return;
    }

    // Attach listeners + observer, but do NO scanning here. The DOM is marked dirty;
    // the first real scan happens on the first pull (hasVisibleViolation) or the first
    // debounced trigger, and only while a placement is visible.
    this.contentDirty = true;
    this.addScrollListener();
    this.addResizeListener();
    this.addMutationObserver();

    this.isInitialised = true;
  }

  /**
   * Lazy, synchronous pull entry point. Returns whether a banned word is currently
   * visible. Scans the DOM only if it has changed since the last scan, and only while
   * the placement is visible. Off-screen placements short-circuit to "safe" with no work.
   */
  public hasVisibleViolation(isPlacementVisible: VisibilityPredicate = () => true): boolean {
    this.isPlacementVisible = isPlacementVisible;

    if (!isPlacementVisible()) {
      return false;
    }

    if (this.contentDirty) {
      this.findOccurrences();
      this.contentDirty = false;
    }

    this.checkVisibility();
    return this.hasVisibleBlockedKeywords;
  }

  /** EXPENSIVE: locate every banned-word occurrence in the selector-scoped content. */
  private findOccurrences(): void {
    this.currentOccurrences = [];
    const rootElements = this.getRootElementsForSearch();

    for (const rootElement of rootElements) {
      const walker = document.createTreeWalker(rootElement, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
          const parent = node.parentElement;
          if (!parent || parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE') {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      });

      let node;
      while ((node = walker.nextNode())) {
        const text = node.textContent;
        if (!text) continue;

        // The containing element and its clipping ancestors are shared by every match in
        // this text node — resolve once.
        const element = node.parentElement;
        let clippers: Element[] | null = null;
        for (const keyword of this.blockedKeywords) {
          const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
          let match;
          while ((match = regex.exec(text)) !== null) {
            if (clippers === null) {
              clippers = this.getClippingAncestors(element);
            }
            const range = document.createRange();
            range.setStart(node, match.index);
            range.setEnd(node, match.index + match[0].length);
            this.currentOccurrences.push({
              word: match[0],
              range: range.cloneRange(),
              element,
              clippers,
            });
          }
        }
      }
    }

    this.observeScrollContainers();
  }

  /**
   * Walk from `el` up to <body>, collecting ancestors that clip their overflow. A word
   * inside such an ancestor is only truly visible when its rect also falls within that
   * ancestor's box — otherwise it's scrolled/clipped out of view (e.g. a carousel item).
   * `getComputedStyle` is called only here (scan time), never on the hot visibility path.
   */
  private getClippingAncestors(el: Element | null): Element[] {
    const clippers: Element[] = [];
    let current = el;
    while (current && current !== document.body) {
      const style = getComputedStyle(current);
      const overflow = style.overflow + style.overflowX + style.overflowY;
      if (/(auto|scroll|hidden|clip)/.test(overflow)) {
        clippers.push(current);
      }
      current = current.parentElement;
    }
    return clippers;
  }

  /**
   * Watch the scrolling containers that hold occurrences, so scrolling a word into/out of
   * a carousel's visible band re-triggers the (debounced) visibility check. Window scroll
   * does not fire for inner scrolling, so without this a clipped word would never update.
   * Rebuilt on every scan; observers are torn down first so rescans don't leak.
   */
  private observeScrollContainers(): void {
    this.disconnectScrollObservers();
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    // Distinct containers that actually scroll (not merely overflow:hidden), each mapped to
    // the occurrence elements it clips, so we observe those elements against that root.
    const containers = new Map<Element, Set<Element>>();
    for (const occurrence of this.currentOccurrences) {
      const element = occurrence.element;
      if (!element) continue;
      for (const clipper of occurrence.clippers) {
        const scrollable =
          clipper.scrollWidth > clipper.clientWidth || clipper.scrollHeight > clipper.clientHeight;
        if (!scrollable) continue;
        let elements = containers.get(clipper);
        if (!elements) {
          elements = new Set();
          containers.set(clipper, elements);
        }
        elements.add(element);
      }
    }

    for (const [root, elements] of containers) {
      try {
        const observer = new IntersectionObserver(() => this.debounce('visibility'), {
          root,
          threshold: [0, 1],
        });
        elements.forEach((el) => observer.observe(el));
        this.scrollObservers.push(observer);
      } catch (error) {
        logger.warn(`Error observing scroll container: ${error}`);
      }
    }
  }

  private disconnectScrollObservers(): void {
    for (const observer of this.scrollObservers) {
      observer.disconnect();
    }
    this.scrollObservers = [];
  }

  private getRootElementsForSearch(): Element[] {
    if (!this.contentSelectors.trim()) {
      return [document.body];
    }

    try {
      const elements = Array.from(document.querySelectorAll(this.contentSelectors));
      return elements.length > 0 ? elements : [document.body];
    } catch (error) {
      logger.warn(
        `Invalid content selectors "${this.contentSelectors}", falling back to document.body: ${error}`
      );
      return [document.body];
    }
  }

  private isInViewport(occurrence: Occurrence): boolean {
    try {
      const rect = occurrence.range.getBoundingClientRect();
      const buffer = VIEWPORT_BUFFER_PX;

      const inWindow =
        rect.top >= -buffer &&
        rect.bottom <= window.innerHeight + buffer &&
        rect.left >= -buffer &&
        rect.right <= window.innerWidth + buffer;
      if (!inWindow) {
        return false;
      }

      // The word may be within the window yet clipped out of a scrolling ancestor
      // (e.g. a carousel/scroller). It is only truly visible if its rect also intersects
      // each clipping ancestor's box.
      for (const clipper of occurrence.clippers) {
        if (!this.rectsIntersect(rect, clipper.getBoundingClientRect(), buffer)) {
          return false;
        }
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  private rectsIntersect(a: DOMRect, b: DOMRect, buffer: number): boolean {
    return (
      a.bottom >= b.top - buffer &&
      a.top <= b.bottom + buffer &&
      a.right >= b.left - buffer &&
      a.left <= b.right + buffer
    );
  }

  /**
   * CHEAP: re-test the already-found occurrences against the viewport. Updates
   * `hasVisibleBlockedKeywords` and dispatches violation/restored events on transitions
   * (drives post-render blur + reporting). Does NOT re-scan the DOM.
   */
  private checkVisibility(): void {
    const visibleWords = this.currentOccurrences
      .filter((occurrence) => this.isInViewport(occurrence))
      .map((occurrence) => occurrence.word);

    const hasVisibleKeywords = visibleWords.length > 0;

    if (hasVisibleKeywords && !this.hasVisibleBlockedKeywords) {
      const uniqueWords = [...new Set(visibleWords)];
      this.hasVisibleBlockedKeywords = true;
      this.dispatch('BrandSafetyComplianceViolation', { detectedWords: uniqueWords });
    } else if (!hasVisibleKeywords && this.hasVisibleBlockedKeywords) {
      this.hasVisibleBlockedKeywords = false;
      this.dispatch('BrandSafetyComplianceRestored', {});
    } else if (hasVisibleKeywords && this.hasVisibleBlockedKeywords) {
      // Still visible, but the visible word set may have changed.
      const uniqueWords = [...new Set(visibleWords)];
      this.dispatch('BrandSafetyComplianceViolation', { detectedWords: uniqueWords });
    }
  }

  private addScrollListener(): void {
    this.scrollHandler = () => this.debounce('visibility');
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  private addResizeListener(): void {
    this.resizeHandler = () => this.debounce('visibility');
    window.addEventListener('resize', this.resizeHandler);
  }

  /**
   * Coalesce scroll/resize/mutation work into a single trailing-edge callback.
   * `'visibility'` re-tests viewport only; `'rescan'` re-finds occurrences first
   * (used after content mutations). Both are gated on placement visibility.
   */
  private debounce(kind: 'visibility' | 'rescan'): void {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    this.debounceTimeout = window.setTimeout(() => {
      this.debounceTimeout = null;
      if (!this.isPlacementVisible()) {
        return;
      }
      if (kind === 'rescan' && this.contentDirty) {
        this.findOccurrences();
        this.contentDirty = false;
      }
      this.checkVisibility();
    }, DEBOUNCE_MS);
  }

  private addMutationObserver(): void {
    if (typeof MutationObserver === 'undefined') {
      return;
    }

    try {
      this.mutationObserver = new MutationObserver((mutations) => {
        let contentChanged = false;

        for (const mutation of mutations) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            for (const node of mutation.addedNodes) {
              if (
                node.nodeType === Node.TEXT_NODE ||
                (node.nodeType === Node.ELEMENT_NODE && (node as Element).textContent)
              ) {
                contentChanged = true;
                break;
              }
            }
          }
          if (mutation.type === 'characterData') {
            contentChanged = true;
          }
          if (contentChanged) break;
        }

        if (contentChanged) {
          // Mark dirty and defer; the actual scan happens on the debounced trailing edge
          // (and only while the placement is visible), or lazily at the next pull.
          this.contentDirty = true;
          this.debounce('rescan');
        }
      });

      this.mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    } catch (error) {
      logger.warn(`Error setting up mutation observer: ${error}`);
    }
  }

  public destroy(): void {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }

    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    this.disconnectScrollObservers();

    this.currentOccurrences = [];
    this.blockedKeywords = [];
    this.hasVisibleBlockedKeywords = false;
    this.contentDirty = true;
    this.isInitialised = false;
  }

  public getCurrentViolationState(): { hasViolation: boolean; detectedWords?: string[] } {
    const hasViolation = this.hasVisibleBlockedKeywords;
    const detectedWords = hasViolation ? this.currentOccurrences.map((occ) => occ.word) : undefined;

    return {
      hasViolation,
      detectedWords,
    };
  }
}

class BrandSafetyManager {
  private static instance: BrandSafetyManager | null = null;
  private brandSafetyInstances: Map<string, BrandSafety> = new Map();

  public static getInstance(): BrandSafetyManager {
    if (!BrandSafetyManager.instance) {
      BrandSafetyManager.instance = new BrandSafetyManager();
    }
    return BrandSafetyManager.instance;
  }

  public getBrandSafetyForCampaign(
    campaignId: string,
    bkwds: string,
    contentSelectors?: string
  ): BrandSafety | null {
    if (!this.brandSafetyInstances.has(campaignId)) {
      try {
        const brandSafety = new BrandSafety(bkwds, contentSelectors);
        this.brandSafetyInstances.set(campaignId, brandSafety);
        brandSafety.activate();
        logger.debug(`Created shared Brand Safety instance for campaign ${campaignId}`);
      } catch (error) {
        logger.error(`Error creating Brand Safety instance for campaign ${campaignId}: ${error}`);
        return null;
      }
    }

    return this.brandSafetyInstances.get(campaignId) || null;
  }

  public getInstanceCount(): number {
    return this.brandSafetyInstances.size;
  }

  // For testing purposes
  public static resetInstance(): void {
    if (BrandSafetyManager.instance) {
      // Clean up all instances
      BrandSafetyManager.instance.brandSafetyInstances.forEach((brandSafety) => {
        brandSafety.destroy();
      });
      BrandSafetyManager.instance.brandSafetyInstances.clear();
      BrandSafetyManager.instance = null;
    }
  }
}

export { BrandSafety, BrandSafetyManager };
export type { BrandSafetyEvents };
